import { pagesQueriesLink, urlInspectionLink } from '../GscTools/links';
import { reportsPosition, rowMetrics } from '../GscTools/metrics';
import { orderRows } from '../PagesQueriesChanges/rows';
import { filterLabel, otherGroupsFor } from './groups';

/**
 * The figures the blocks draw, derived from what came back rather than fetched again, plus the
 * links that carry one exact URL into the tools that answer the next question about it.
 */

/**
 * Block 02's rows: one per group that came back, each against its own baseline.
 *
 * `comparable` is set only where both windows returned the group, which is what makes the shared
 * comparison columns print a movement rather than "not comparable". A window that returned nothing
 * is unknown for that group, not zero, and a baseline of zero clicks has an absolute change but no
 * percentage. There is no total row: groups can overlap, so their sum counts some pages twice.
 */
export function comparisonRows(scope, totals) {
    return scope.groups
        .filter((group) => totals[group.name]?.status === 'done')
        .map((group) => {
            const current = totals[group.name].current;
            const prior = totals[group.name].baseline;
            const comparable = !!current && !!prior;

            return {
                id: group.name,
                key: group.name,
                // Carried on the row so the export says which filters produced it.
                filters: group.rules.map(filterLabel),
                current,
                prior,
                comparable,
                deltaClicks: comparable ? current.clicks - prior.clicks : null,
                deltaPercent:
                    comparable && prior.clicks > 0
                        ? (100 * (current.clicks - prior.clicks)) / prior.clicks
                        : null,
            };
        });
}

/** Block 02's rows in the applied mode's order, through the same ordering Pages & Queries uses. */
export function orderedComparisonRows(scope, totals) {
    return orderRows(comparisonRows(scope, totals), { mode: scope.mode, metric: scope.metric });
}

/**
 * Block 04's rows: the matching pages as returned, each naming the other groups it also meets,
 * highest first in the applied metric.
 */
export function pageRows(scope, rows, groupName) {
    const hasPosition = reportsPosition(scope.searchType);
    const metric = scope.metric;

    return (rows ?? [])
        .map((row) => {
            const key = row?.keys?.[0] ?? '';
            return {
                id: key,
                key,
                ...rowMetrics(row, hasPosition),
                alsoIn: otherGroupsFor(key, scope.groups, groupName),
            };
        })
        .sort((a, b) => b[metric] - a[metric] || (a.key < b.key ? -1 : 1));
}

/** The URL Inspection link for one exact URL, filled in but not inspected. */
export function inspectionLink(scope, url) {
    return urlInspectionLink(scope.siteUrl, url);
}

/**
 * The page-filtered detail for one exact URL: Pages, Queries & Changes on its queries view, under
 * this report's own scope and a single `page equals` rule for the URL exactly as Search Console
 * returned it.
 */
export function pageQueriesLink(scope, url, period) {
    return pagesQueriesLink(scope, period, {
        view: 'queries',
        rules: [{ dimension: 'page', operator: 'equals', expression: url }],
    });
}
